import type { AnalysisRunHistoryRow } from '../../../services/AnalysisApiClient';
import type { AnalysisRunRow } from '../../../services/FirmCompaniesApiClient';

/** Normalized row for the shared run-history / snapshot table (client + firm). */
export type RunHistoryTableRow = {
  rowKey: string;
  analysisId: string;
  runId: string;
  analysisName: string;
  runDate: string;
  status: string;
  ranByName?: string;
  ranByEmail?: string;
  individualsCount: number;
  overCount: number;
  hasSnapshot: boolean;
};

export function mapClientRunHistoryToTableRows(
  currentAnalysisId: string,
  rows: AnalysisRunHistoryRow[]
): RunHistoryTableRow[] {
  const aid = String(currentAnalysisId ?? '').trim();
  return rows.map((r, i) => {
    const runId = String(r.runId ?? '').trim();
    return {
      rowKey: runId || `client-${aid}-${i}-${r.runDate}-${r.status}`,
      analysisId: aid,
      runId,
      analysisName: r.analysisName ?? '',
      runDate: r.runDate,
      status: r.status,
      ranByName: r.ranByName,
      ranByEmail: r.ranByEmail,
      individualsCount: r.individualsCount,
      overCount: r.overCount,
      hasSnapshot: r.hasSnapshot === true,
    };
  });
}

export function mapFirmCompanyRunHistoryToTableRows(rows: AnalysisRunRow[]): RunHistoryTableRow[] {
  return rows.map((r, i) => {
    const runId = String(r.id ?? '').trim();
    const analysisId = String(r.analysisId ?? '').trim();
    return {
      rowKey: runId || `firm-${i}-${r.runDate}-${r.status}`,
      analysisId,
      runId,
      analysisName: r.analysisName ?? '',
      runDate: r.runDate,
      status: r.status,
      ranByName: r.ranByName,
      ranByEmail: r.ranByEmail,
      individualsCount: r.individualsCount,
      overCount: r.overCount,
      hasSnapshot: r.hasSnapshot === true,
    };
  });
}

export function filterRunHistoryTableRows(rows: RunHistoryTableRow[], searchQuery: string): RunHistoryTableRow[] {
  const searchLower = (searchQuery ?? '').trim().toLowerCase();
  if (!searchLower) return rows;

  return rows.filter((r) => {
    const analysis = String(r.analysisName ?? '').toLowerCase();
    const runDateRaw = String(r.runDate ?? '').toLowerCase();
    const status = String(r.status ?? '').toLowerCase();
    const ranByName = String(r.ranByName ?? '').toLowerCase();
    const ranByEmail = String(r.ranByEmail ?? '').toLowerCase();
    const individuals = String(r.individualsCount ?? '').toLowerCase();
    const over = String(r.overCount ?? '').toLowerCase();
    const runIdSearch = String(r.runId ?? '').toLowerCase();
    const analysisIdSearch = String(r.analysisId ?? '').toLowerCase();

    const runDateDisplay = (() => {
      const d = new Date(r.runDate ?? '');
      if (Number.isNaN(d.getTime())) return String(r.runDate ?? '');
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
      });
    })();

    const runDateDisplayLower = String(runDateDisplay ?? '').toLowerCase();

    return (
      analysis.includes(searchLower) ||
      runDateRaw.includes(searchLower) ||
      runDateDisplayLower.includes(searchLower) ||
      status.includes(searchLower) ||
      ranByName.includes(searchLower) ||
      ranByEmail.includes(searchLower) ||
      individuals.includes(searchLower) ||
      over.includes(searchLower) ||
      runIdSearch.includes(searchLower) ||
      analysisIdSearch.includes(searchLower)
    );
  });
}
